import React, { StrictMode } from "react";
import {
    Link,
    Route,
    RouterProvider,
    createBrowserRouter,
    createRoutesFromElements,
} from "react-router-dom";

import { Amplify } from "aws-amplify";
import { parseAmplifyConfig } from "aws-amplify/utils";

import outputs from "@/amplify_outputs.json";
import * as Sentry from "@sentry/react";

import Layout from "@components/Layout";

import layoutLoader from "@loaders/LayoutLoader";

import { ErrorHandler } from "@services/errorHandling";

import "./assets/css/majorybl.scss";
import ErrorBoundary from "./components/ErrorBoundary";
import RequireAuth from "./components/RequireAuth";
import TestEditor from "./pages/TestEditor";
import ClubhouseRoutes from "./routes/Clubhouse";
import IndexRoutes from "./routes/Index";
import LeagueManagementRoutes from "./routes/LeagueManagement";
import LeaguesRoutes from "./routes/Leagues";
import OrgManagementRoutes from "./routes/OrgManagement";
import SiteManagementRoutes from "./routes/SiteManagement";

Amplify.configure({
    ...parseAmplifyConfig(outputs),
    Auth: {
        Cognito: {
            identityPoolId: process.env.REACT_APP_LOGGER_POOL!,
            allowGuestAccess: true,
        },
    },
});

// Initialize Sentry first
Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.BUILD_ENV,
    beforeSend(event) {
        if (process.env.BUILD_ENV !== "production") {
            return null;
        }
        return event;
    },
});

// Initialize auto error handling
ErrorHandler.initialize();

const App = (): React.ReactElement => {
    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route
                path="/"
                element={<Layout />}
                loader={layoutLoader}
                errorElement={<ErrorBoundary />}
                handle={{ crumb: () => <Link to="/">Home</Link> }}
            >
                {IndexRoutes}
                {LeaguesRoutes}
                {ClubhouseRoutes}
                {LeagueManagementRoutes}
                {OrgManagementRoutes}
                {SiteManagementRoutes}
                {process.env.BUILD_ENV === "development" && (
                    <Route element={<RequireAuth allowedUserLevels={[1]} />}>
                        <Route path="testeditor" element={<TestEditor />} />
                    </Route>
                )}
            </Route>,
        ),
    );

    return (
        <StrictMode>
            <RouterProvider router={router} />
        </StrictMode>
    );
};

export default App;
